import { useMemo, useState } from "react";
import { Upload } from "lucide-react";
import type { CensusImportPreview, CensusImportRow, MemberDraft } from "../../types";
import { RELATIONS, type Relation } from "../../types";
import { getApi } from "../../api";
import { reportError } from "../../services/eventBus";
import { Card } from "../Card";
import { Button } from "../Button";
import { Alert } from "../Alert";
import { DataGrid, type GridColumn } from "../DataGrid";
import { IconButton } from "../IconButton";
import { FileUpload } from "../fields/FileUpload";
import { formatDate, formatHeight } from "../../utils/format";
import { Trash2 } from "lucide-react";

interface CensusImportCardProps {
  importedCount: number;
  onImport: (members: readonly MemberDraft[]) => void;
  children?: never;
}

function splitName(name: string): { firstName: string; lastName: string } {
  const trimmed = name.trim();
  const index = trimmed.lastIndexOf(" ");
  if (index === -1) return { firstName: trimmed, lastName: "" };
  return { firstName: trimmed.slice(0, index), lastName: trimmed.slice(index + 1) };
}

function toDraft(row: CensusImportRow): MemberDraft {
  const { firstName, lastName } = splitName(row.name);
  return {
    firstName,
    lastName,
    relation: (RELATIONS as readonly string[]).includes(row.relation) ? (row.relation as Relation) : "Employee",
    subscriberMemberId: null,
    gender: row.gender === "M" || row.gender === "F" ? row.gender : null,
    dateOfBirth: row.dateOfBirth,
    heightFeet: row.heightFeet,
    heightInches: row.heightInches,
    weightLbs: row.weightLbs,
    postalCode: null,
    ssn: row.ssn,
    notes: row.notes,
    memberType: "Real",
    externalMemberId: row.externalId,
  };
}

/** Census Creator workbook upload with a validated preview; invalid rows block Import until removed. */
export function CensusImportCard({ importedCount, onImport }: CensusImportCardProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [preview, setPreview] = useState<CensusImportPreview | null>(null);
  const invalidCount = preview?.rows.filter((row) => row.errors.length > 0).length ?? 0;

  const handleFile = async (next: File | null) => {
    setFile(next);
    setPreview(null);
    if (!next) return;
    setIsParsing(true);
    try {
      setPreview(await getApi().importCensusPreview(next));
    } catch (error) {
      reportError(error, "CensusImportCard.parse");
      setFile(null);
    } finally {
      setIsParsing(false);
    }
  };

  const handleRemoveRow = (rowNumber: number) => setPreview((current) => (current ? { ...current, rows: current.rows.filter((row) => row.rowNumber !== rowNumber) } : current));

  const handleImport = () => {
    if (!preview) return;
    onImport(preview.rows.map(toDraft));
    setPreview(null);
    setFile(null);
  };

  const handleCancel = () => {
    setPreview(null);
    setFile(null);
  };

  const columns = useMemo<GridColumn<CensusImportRow>[]>(
    () => [
      { key: "name", header: "Name", render: (row) => row.name || <span className="text-danger">Missing</span> },
      { key: "relation", header: "Relation", render: (row) => row.relation },
      { key: "gender", header: "Gender", render: (row) => row.gender ?? "" },
      { key: "dob", header: "DOB", render: (row) => formatDate(row.dateOfBirth) },
      { key: "height", header: "Height", render: (row) => formatHeight(row.heightFeet, row.heightInches) },
      { key: "weight", header: "Weight", align: "right", render: (row) => row.weightLbs ?? "" },
      { key: "notes", header: "Notes", render: (row) => row.notes ?? "" },
      { key: "validation", header: "Validation", render: (row) => (row.errors.length === 0 ? <span className="text-success">Ready</span> : <span className="text-danger">{row.errors.join(" ")}</span>) },
    ],
    [],
  );

  return (
    <Card title="Import Census" icon={Upload}>
      <p className="mb-3 text-sm text-gray-600">Upload a Census Creator workbook. The importer reads the MUGS tab and previews the members before anything is saved.</p>
      {importedCount > 0 && <Alert type="success" message={`${importedCount} member${importedCount === 1 ? "" : "s"} will be created with this group.`} className="mb-3" />}
      <FileUpload id="census-file" label="Census Creator workbook" acceptExtensions={["xlsm", "xlsx"]} file={file} onFile={(next) => void handleFile(next)} isProcessing={isParsing} hint="The Census Creator is macro-enabled (.xlsm); a plain .xlsx with the same MUGS tab works too." />
      {preview && (
        <div className="mt-4">
          {invalidCount > 0 && <Alert type="warning" message={`${invalidCount} row${invalidCount === 1 ? "" : "s"} need attention. Fix the workbook or remove the rows to enable Import.`} className="mb-3" />}
          <DataGrid
            caption={`Census preview from ${preview.fileName}`}
            columns={columns}
            rows={preview.rows}
            rowKey={(row) => String(row.rowNumber)}
            rowClassName={(row) => (row.errors.length > 0 ? "bg-danger-bg" : "")}
            renderActions={(row) => <IconButton icon={Trash2} label={`Remove row ${row.rowNumber}`} tone="danger" onClick={() => handleRemoveRow(row.rowNumber)} />}
            emptyState={<Alert type="info" message="Every row was removed. Upload another workbook to try again." />}
          />
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleImport} disabled={invalidCount > 0 || preview.rows.length === 0}>
              Import {preview.rows.length} member{preview.rows.length === 1 ? "" : "s"}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
